import { Color } from '@persona-id/design-tokens';
import { useState } from 'react';
import styled from 'styled-components';

import TableLayout, {
  EmbeddedTableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import {
  formatCompanyName,
  formatCompanyPosition,
  nameCase,
} from 'dashboard/lib/utils/stringFormat';
import { AssociatedPerson, ReportBusinessLookup } from 'dashboard/models/report/business-lookup';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import * as Spacing from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';

enum PeoplePage {
  AssociatedPeople = 'associated_people',
  RegisteredAgents = 'registered_agents',
}

const Tabs = styled('div')`
  display: flex;
  flex-direction: row;
  font-weight: ${FontWeight.Medium};
  margin-bottom: ${Spacing.BASE * 3}px;

  & > div {
    margin-right: ${Spacing.BASE * 3}px;
  }
`;

const Tab = styled('div')<{ active: boolean }>`
  color: ${({ active }) => (active ? Color.CorePurple800 : Color.CoreSlate800)};
  padding-bottom: ${Spacing.BASE * 1.5}px;
  border-bottom: ${({ active }) => (active ? '4px' : '0')} solid ${Color.CorePurple800};

  cursor: pointer;
`;

const CountSticker = styled(Sticker)`
  margin-left: ${Spacing.BASE * 0.5}px;
  position: relative;
  top: -1px;
`;

const StickyTableTH = styled(TableTH)`
  position: sticky;
  top: 0;
`;

interface BusinessPeopleCardProps {
  report: ReportBusinessLookup;
}

const BusinessLookupPeople = ({ report }: BusinessPeopleCardProps) => {
  const [peoplePage, setPeoplePage] = useState(PeoplePage.AssociatedPeople);
  const result = report.attributes.result;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const associatedPeople = result.officers;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const registeredAgents = result.agents.map((agent) => agent['name-full']);

  const body = (
    <>
      <Tabs>
        <Tab
          id='report-details__people--associated-people'
          active={peoplePage === PeoplePage.AssociatedPeople}
          onClick={setPeoplePage.bind(null, PeoplePage.AssociatedPeople)}
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          Associated People <CountSticker>{associatedPeople.length}</CountSticker>
        </Tab>
        <Tab
          id='report-details__people--registered-agents'
          active={peoplePage === PeoplePage.RegisteredAgents}
          onClick={setPeoplePage.bind(null, PeoplePage.RegisteredAgents)}
        >
          Registered Agents <CountSticker>{registeredAgents.length}</CountSticker>
        </Tab>
      </Tabs>
      {peoplePage === PeoplePage.AssociatedPeople && (
        <TableLayout
          dataTest='associated-people-table'
          showHeader={true}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          isEmpty={associatedPeople.length === 0}
          nullState={
            <EmbeddedTableNullState
              marginVertical={Spacing.BASE * 2}
              dataTestBlock='associated-people-table'
            >
              No associated people found
            </EmbeddedTableNullState>
          }
          headerTR={
            <>
              <TableTH>Name</TableTH>
              <TableTH>Position</TableTH>
            </>
          }
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {associatedPeople.map((associatedPerson: AssociatedPerson) => {
            return (
              <TableTR key={associatedPerson['name-full']}>
                <TableTD>{nameCase(associatedPerson['name-full'])}</TableTD>
                <TableTD>
                  {(associatedPerson.titles || []).length > 0
                    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      associatedPerson.titles
                        .map((title) => formatCompanyPosition(title))
                        .join(', ')
                    : 'Unknown'}
                </TableTD>
              </TableTR>
            );
          })}
        </TableLayout>
      )}
      {peoplePage === PeoplePage.RegisteredAgents && (
        <TableLayout
          containerMaxHeight={470}
          dataTest='registered-agents-table'
          showHeader={true}
          isEmpty={registeredAgents.length === 0}
          nullState={
            <EmbeddedTableNullState
              marginVertical={Spacing.BASE}
              dataTestBlock='registered-agents-table'
            >
              No registered agents found
            </EmbeddedTableNullState>
          }
          headerTR={
            <>
              <StickyTableTH>Name</StickyTableTH>
            </>
          }
        >
          <>
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            {registeredAgents.sort().map((registeredAgent: string) => {
              return (
                <TableTR key={registeredAgent}>
                  <TableTD>{formatCompanyName(registeredAgent)}</TableTD>
                </TableTR>
              );
            })}
          </>
        </TableLayout>
      )}
    </>
  );

  return (
    <>
      <Card.Title>People</Card.Title>
      {body}
    </>
  );
};

export default BusinessLookupPeople;
